import "@/App.css";
import { useEffect, useRef } from "react";
import { BrowserRouter, Routes, Route, Navigate, useLocation, useParams } from "react-router-dom";
import { Toaster } from "@/components/ui/sonner";
import { TooltipProvider } from "@/components/ui/tooltip";
import { AuthProvider, useAuth } from "@/contexts/AuthContext";
import { SettingsProvider } from "@/contexts/SettingsContext";
import { ComparisonProvider } from "@/contexts/ComparisonContext";
import { UnreadRespuestasProvider, useUnreadRespuestas } from "@/contexts/UnreadRespuestasContext";
import { UnreadRevisionesProvider, useUnreadRevisiones } from "@/contexts/UnreadRevisionesContext";
import { UnopenedCandidatosProvider, useUnopenedCandidatos } from "@/contexts/UnopenedCandidatosContext";
import { ChatProvider } from "@/contexts/ChatContext";
import { getFormularios, getFormularioRespuestas, getCandidatos, getRevisiones, getRevisionTimeline } from "@/lib/api";
import { CursorPresence } from "@/components/CursorPresence";
import { CustomCursor } from "@/components/CustomCursor";
import { AppBackground } from "@/components/AppBackground";
import { useIsMobile, useIsTouch } from "@/hooks/use-mobile";
import HomePage from "@/pages/HomePage";
import LoginPage from "@/pages/LoginPage";
import CandidatosPage from "@/pages/CandidatosPage";
import CandidatoDetailPage from "@/pages/CandidatoDetailPage";
import ProveedoresPage from "@/pages/ProveedoresPage";
import ProveedorDetailPage from "@/pages/ProveedorDetailPage";
import CompararPage from "@/pages/CompararPage";
import LegalPage from "@/pages/LegalPage";
import DashboardPage from "@/pages/DashboardPage";
import FormulariosLayout from "@/pages/FormulariosLayout";
import FormularioDetailPage from "@/pages/FormularioDetailPage";
import RevisionesLayout from "@/pages/RevisionesLayout";
import RevisionDetailPage from "@/pages/RevisionDetailPage";
import MetricasLayout from "@/pages/MetricasLayout";
import MetricasDetailPage from "@/pages/MetricasDetailPage";
import CorreosLayout from "@/pages/CorreosLayout";
import RealizarFormularioPage from "@/pages/RealizarFormularioPage";
import RespuestaCompartidaPage from "@/pages/RespuestaCompartidaPage";
import RevisionPublicSharePage from "@/pages/RevisionPublicSharePage";
import RevisionRespuestaCompartidaPage from "@/pages/RevisionRespuestaCompartidaPage";
import BrutalFilesPage from "@/pages/BrutalFilesPage";
import BrutalFilesPublicSharePage from "@/pages/BrutalFilesPublicSharePage";
import OnboardingLayout from "@/pages/OnboardingLayout";
import OnboardingDetailPage from "@/pages/OnboardingDetailPage";
import OnboardingPublicPage from "@/pages/OnboardingPublicPage";
import GmbReviewsPage from "@/pages/GmbReviewsPage";
import ArtefactosPage from "@/pages/ArtefactosPage";
import MaterialPage from "@/pages/MaterialPage";
import MaterialPublicPage from "@/pages/MaterialPublicPage";
import MaterialEtiquetasPage from "@/pages/MaterialEtiquetasPage";
import ClientesPage from "@/pages/ClientesPage";
import ClienteDetailPage from "@/pages/ClienteDetailPage";
import ChatLayout, { ChatEmptyState } from "@/pages/ChatLayout";
import ChatConversacion from "@/pages/ChatConversacion";
import MisTrabajosPage from "@/pages/MisTrabajosPage";

function FormulariosEmptyState() {
  return (
    <div className="flex items-center justify-center min-h-[40vh] px-6 text-center">
      <p className="text-muted-foreground">
        Selecciona un formulario de la lista y pulsa «Editar» para verlo aquí.
      </p>
    </div>
  );
}

function MetricasEmptyState() {
  return (
    <div className="flex items-center justify-center min-h-[40vh] px-6 text-center">
      <p className="text-muted-foreground">
        Selecciona un cliente de la lista o crea uno nuevo para ver el dashboard.
      </p>
    </div>
  );
}

function RevisionesEmptyState() {
  return (
    <div className="flex items-center justify-center min-h-[40vh] px-6 text-center">
      <p className="text-muted-foreground">
        Selecciona una hoja de trabajo de la lista y empieza a gestionar sus revisiones.
      </p>
    </div>
  );
}

function OnboardingEmptyState() {
  return (
    <div className="flex items-center justify-center min-h-[40vh] px-6 text-center">
      <p className="text-muted-foreground">
        Selecciona una presentación de la lista o crea una nueva con «Nueva presentación».
      </p>
    </div>
  );
}

/** Remonta el detalle al cambiar :id para no mostrar datos del ítem anterior (mismo patrón que navegar entre candidatos con estado limpio). */
function FormularioDetailRoute() {
  const { id } = useParams();
  return <FormularioDetailPage key={id} />;
}

function ChatConversacionRoute() {
  const { clienteId } = useParams();
  return <ChatConversacion key={clienteId} />;
}

function RevisionDetailRoute() {
  const { id } = useParams();
  return <RevisionDetailPage key={id} />;
}

function OnboardingDetailRoute() {
  const { id } = useParams();
  return <OnboardingDetailPage key={id} />;
}

/** Precarga los conteos de respuestas no leídas para que los badges aparezcan en header y sidebar sin entrar a un formulario. */
function UnreadRespuestasPreload() {
  const { user } = useAuth();
  const { setBulkUnreadByFormulario, readRespuestaIdsByFormulario } = useUnreadRespuestas();
  const didPreload = useRef(false);

  useEffect(() => {
    if (user?.permissions?.formularios === false) {
      didPreload.current = false;
      return;
    }
    if (!user) return;
    if (didPreload.current) return;
    didPreload.current = true;
    getFormularios()
      .then((data) => {
        const formularios = data.formularios || [];
        if (formularios.length === 0) return;
        return Promise.all(
          formularios.map((f) =>
            getFormularioRespuestas(f.id)
              .then((d) => {
                const all = d.respuestas || [];
                const readByFormulario = readRespuestaIdsByFormulario[f.id] || {};
                const count = all.filter((r) => !readByFormulario[r.id]).length;
                return { id: f.id, count };
              })
              .catch(() => ({ id: f.id, count: 0 }))
          )
        );
      })
      .then((results) => {
        if (!results) return;
        const bulk = {};
        results.forEach(({ id, count }) => {
          bulk[id] = count;
        });
        setBulkUnreadByFormulario(bulk);
      })
      .catch(() => {});
  }, [user, setBulkUnreadByFormulario, readRespuestaIdsByFormulario]);

  return null;
}

/** Precarga el conteo de candidatos no abiertos para el badge del header. */
function UnopenedCandidatosPreload() {
  const { user } = useAuth();
  const { syncUnopenedFromCandidatoIds } = useUnopenedCandidatos();
  const didPreload = useRef(false);

  useEffect(() => {
    if (user?.permissions?.candidatos === false) {
      didPreload.current = false;
      return;
    }
    if (!user) return;
    if (didPreload.current) return;
    didPreload.current = true;
    getCandidatos({})
      .then((data) => {
        const list = Array.isArray(data?.candidatos) ? data.candidatos : [];
        const ids = list.map((c) => c.id).filter(Boolean);
        syncUnopenedFromCandidatoIds(ids);
      })
      .catch(() => {});
  }, [user, syncUnopenedFromCandidatoIds]);

  return null;
}

function UnreadRevisionesPreload() {
  const { user } = useAuth();
  const { setBulkUnreadByRevision, readTimelineIdsByRevision } = useUnreadRevisiones();
  const didPreload = useRef(false);

  useEffect(() => {
    if (user?.permissions?.revisiones === false) {
      didPreload.current = false;
      return;
    }
    if (!user || didPreload.current) return;
    didPreload.current = true;
    getRevisiones()
      .then((data) => {
        const revisiones = data.revisiones || [];
        if (revisiones.length === 0) return;
        return Promise.all(
          revisiones.map((r) =>
            getRevisionTimeline(r.id)
              .then((d) => {
                const events = (d.timeline || []).filter((e) => e?.tipo === "respuesta_cliente");
                const read = readTimelineIdsByRevision[r.id] || {};
                const count = events.filter((e) => !read[e.id]).length;
                return { id: r.id, count };
              })
              .catch(() => ({ id: r.id, count: 0 }))
          )
        );
      })
      .then((results) => {
        if (!results) return;
        const bulk = {};
        results.forEach(({ id, count }) => {
          bulk[id] = count;
        });
        setBulkUnreadByRevision(bulk);
      })
      .catch(() => {});
  }, [user, setBulkUnreadByRevision, readTimelineIdsByRevision]);

  return null;
}

function AnimatedRoutes() {
  return (
    <div className="min-h-full">
      <Routes>
            <Route path="/" element={<Navigate to="/login" replace />} />
            <Route path="/login" element={<LoginPage />} />
            <Route path="/aplicar" element={<HomePage />} />
            <Route path="/candidatos" element={<CandidatosPage />} />
            <Route path="/candidatos/:id" element={<CandidatoDetailPage />} />
            <Route path="/proveedores" element={<ProveedoresPage />} />
            <Route path="/proveedores/:id" element={<ProveedorDetailPage />} />
            <Route path="/comparar" element={<CompararPage />} />
            <Route path="/dashboard" element={<DashboardPage />} />
            <Route path="/formularios" element={<FormulariosLayout />}>
              <Route index element={<FormulariosEmptyState />} />
              <Route path=":id" element={<FormularioDetailRoute />} />
            </Route>
            <Route path="/revisiones" element={<RevisionesLayout />}>
              <Route index element={<RevisionesEmptyState />} />
              <Route path=":id" element={<RevisionDetailRoute />} />
            </Route>
            <Route path="/onboarding" element={<OnboardingLayout />}>
              <Route index element={<OnboardingEmptyState />} />
              <Route path=":id" element={<OnboardingDetailRoute />} />
            </Route>
            <Route path="/metricas" element={<MetricasLayout />}>
              <Route index element={<MetricasEmptyState />} />
              <Route path=":clientId" element={<MetricasDetailPage />} />
            </Route>
            <Route path="/correos" element={<CorreosLayout />} />
            <Route path="/clientes" element={<ClientesPage />} />
            <Route path="/clientes/:id" element={<ClienteDetailPage />} />
            <Route path="/chat" element={<ChatLayout />}>
              <Route index element={<ChatEmptyState />} />
              <Route path=":clienteId" element={<ChatConversacionRoute />} />
            </Route>
            <Route path="/trabajos" element={<MisTrabajosPage />} />
            <Route path="/gmb" element={<GmbReviewsPage />} />
            <Route path="/artefactos" element={<ArtefactosPage />} />
            <Route path="/material" element={<MaterialPage />} />
            <Route path="/material/etiquetas" element={<MaterialEtiquetasPage />} />
            <Route path="/m/:etiqueta" element={<MaterialPublicPage />} />
            <Route path="/f/:slug" element={<RealizarFormularioPage />} />
            <Route path="/r/:shareId" element={<RespuestaCompartidaPage />} />
            <Route path="/rr/:shareId" element={<RevisionRespuestaCompartidaPage />} />
            <Route path="/rv/:shareId" element={<RevisionPublicSharePage />} />
            <Route path="/legal" element={<LegalPage />} />
            <Route path="/brutalfiles" element={<BrutalFilesPage />} />
            <Route path="/brutalfiles/share/:shareId" element={<BrutalFilesPublicSharePage />} />
            <Route path="/ob/:shareSlug" element={<OnboardingPublicPage />} />
            <Route path="*" element={<Navigate to="/login" replace />} />
      </Routes>
    </div>
  );
}

function AppChrome() {
  const location = useLocation();
  const isTouch = useIsTouch();
  const isPublicBrutalShare = location.pathname.startsWith("/brutalfiles/share/");
  const isPublicOnboardingShare = location.pathname.startsWith("/ob/");
  const isMaterialSinChrome = location.pathname.startsWith("/m/") || location.pathname === "/material/etiquetas";
  if (isPublicBrutalShare || isPublicOnboardingShare || isMaterialSinChrome) {
    return <AnimatedRoutes />;
  }
  return (
    <>
      <AppBackground />
      <CursorPresence />
      {!isTouch && <CustomCursor />}
      <div className="relative z-10">
        <AnimatedRoutes />
      </div>
    </>
  );
}

function App() {
  const isMobile = useIsMobile();
  return (
    <div className="App min-h-dvh bg-background">
      <BrowserRouter>
        <TooltipProvider delayDuration={300}>
        <AuthProvider>
        <SettingsProvider>
        <ComparisonProvider>
          <UnreadRespuestasProvider>
          <UnreadRevisionesProvider>
          <UnopenedCandidatosProvider>
          <ChatProvider>
          <UnreadRespuestasPreload />
          <UnreadRevisionesPreload />
          <UnopenedCandidatosPreload />
          <AppChrome />
          </ChatProvider>
          </UnopenedCandidatosProvider>
          </UnreadRevisionesProvider>
          </UnreadRespuestasProvider>
        </ComparisonProvider>
        </SettingsProvider>
        </AuthProvider>
        </TooltipProvider>
      </BrowserRouter>
      <Toaster position={isMobile ? "top-center" : "bottom-right"} />
    </div>
  );
}

export default App;
